import { Component, inject, OnInit, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { PopoverModule } from 'primeng/popover';
import { AvatarModule } from 'primeng/avatar';
import { TranslocoModule } from '@jsverse/transloco';
import { AuthService } from 'src/app/auth/services/auth-service/auth.service';
import { CurrentUserService } from '../../services/current-user.service';
import { PackLimitationsService } from '../../services/pack-limitations.service';
import { ObjectivesCompletionService } from '../../services/objectives-completion.service';
import { LimitedFeatures } from '../../models/pack.models';

export interface MobileMenuItem {
  label: string;
  icon: string;
  route: string;
  isActive?: boolean;
  testId?: string;
  isLocked?: boolean;
  /** Shows a warning dot on the icon — something is missing on the target page. */
  warning?: boolean;
  /** Translation key describing the warning, used as its accessible label. */
  warningLabel?: string;
}

@Component({
  selector: 'app-mobile-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LucideAngularModule,
    PopoverModule,
    AvatarModule,
    TranslocoModule,
  ],
  templateUrl: './mobile-toolbar.component.html',
})
export class MobileToolbarComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);
  private currentUserService = inject(CurrentUserService);
  protected packLimitationService = inject(PackLimitationsService);
  private objectivesCompletionService = inject(ObjectivesCompletionService);

  // Utilisation des signals du service partagé
  currentUser = this.currentUserService.currentUser;
  isSuperAdmin = this.currentUserService.isSuperAdmin;
  isLoading = this.currentUserService.isLoading;

  mainItems: MobileMenuItem[] = [
    // {
    //   label: 'navigation.home',
    //   icon: 'home',
    //   route: '/home/dashboard-temp',
    //   testId: 'menu.dashboard',
    // },
    {
      label: 'navigation.trips',
      icon: 'image',
      route: '/home/travel-board',
      testId: 'menu.travelBoard',
    },
    // {
    //   label: 'navigation.calculate',
    //   icon: 'calculator',
    //   route: '/home/calculators',
    //   testId: 'menu.calculators',
    // },
  ];

  baseSecondaryItems: MobileMenuItem[] = [];

  // Computed signal pour les éléments secondaires avec super-admin
  secondaryItems = computed(() => {
    const items = [...this.baseSecondaryItems];
    if (this.isSuperAdmin()) {
      items.unshift({
        label: 'navigation.admin',
        icon: 'shield',
        route: '/admin',
        testId: 'menu.admin',
      });
    }
    return items;
  });
  clientFeature = LimitedFeatures.Client;
  constructor() {
    effect(() => {
      const items: MobileMenuItem[] = [
        {
          label: 'navigation.clients',
          icon: 'users',
          route: '/home/clients',
          testId: 'menu.clients',
          isLocked: !this.packLimitationService.canFilterClient(),
        },
        // {
        //   label: 'navigation.resources',
        //   icon: 'book',
        //   route: '/home/resources',
        //   testId: 'menu.resources',
        // },
        {
          label: 'navigation.objectives',
          icon: 'target',
          route: '/home/objectives',
          testId: 'menu.objectives',
          warning: this.objectivesCompletionService.needsAttention(),
          warningLabel: 'navigation.objectivesIncomplete',
        },
        {
          label: 'navigation.factors',
          icon: 'calculator',
          route: '/home/factors',
          testId: 'menu.factors',
        },
        {
          label: 'navigation.help',
          icon: 'help-circle',
          route: '/home/help',
          testId: 'menu.help',
        },
        {
          label: 'navigation.settings',
          icon: 'settings',
          route: '/home/account',
          testId: 'menu.settings',
        },
      ];

      if (this.isSuperAdmin()) {
        items.splice(1, 0, {
          label: 'navigation.contributions',
          icon: 'leaf',
          route: '/home/contributions',
          testId: 'menu.contributions',
        });
      }

      this.baseSecondaryItems = items;
    });
  }
  ngOnInit(): void {
    // Charger les informations utilisateur via le service partagé
    this.currentUserService.loadCurrentUser().subscribe();
  }

  isActive(route: string): boolean {
    return this.router.url === route;
  }

  onAccountClick(): void {
    this.router.navigate(['/home/account']);
  }

  onLogoutClick(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/auth/login']);
      },
      error: (err: Error) => {
        console.error('Logout error', err);
      },
    });
  }
}
